import { useCallback, useMemo, useState } from "react";
import { useCurrentUser } from "@/lib/auth";
import { toast } from "@/lib/toast";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import { NoteReferenceModal } from "@/projectOverview/notes/NoteReferenceModal";
import {
  canOpenNoteReference,
  noteReferencePermissionMessage,
} from "@/projectOverview/notes/noteReferencePermissions";
import type { NoteRefType } from "@/projectOverview/notes/types";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";

const MODAL_REF_TYPES = new Set<NoteRefType | string>(["employee"]);

export function useNoteReferenceAction(contextOrderId = 0) {
  const { user } = useCurrentUser();
  const { open: openProjectOverview } = useProjectOverview();
  const { openDetail: openTimesheetDetail } = useTimesheetFlow();
  const { openDetailByBestellnummer } = useOrderWizard();
  const { open: openDocumentEditor } = useDocumentEditor();
  const [fallbackRef, setFallbackRef] = useState<{
    type: NoteRefType | string;
    id: string;
    label: string;
  } | null>(null);

  const groups = useMemo(
    () => (user?.groups as string[] | undefined) ?? [],
    [user?.groups],
  );

  const handleRefClick = useCallback(
    async (type: NoteRefType | string, refId: string, label: string) => {
      const allowed = await canOpenNoteReference(type, user, groups);
      if (!allowed) {
        toast.error(noteReferencePermissionMessage(type));
        return;
      }

      if (type === "auftrag") {
        const id = Number(refId);
        if (!Number.isFinite(id) || id <= 0) return;
        openProjectOverview(id);
        return;
      }

      if (type === "order") {
        await openDetailByBestellnummer(refId);
        return;
      }

      if (type === "timesheet") {
        const id = Number(refId);
        if (!Number.isFinite(id) || id <= 0) return;
        openTimesheetDetail(id);
        return;
      }

      if (type === "geschaeftsdokument") {
        const id = Number(refId);
        if (!Number.isFinite(id) || id <= 0) return;
        openDocumentEditor({ docId: id });
        return;
      }

      if (type === "material_anforderung") {
        const id = Number(refId);
        if (!Number.isFinite(id) || id <= 0) return;
        // Deep-Link öffnet die MA direkt (wie Querverweise im Bestellwesen).
        window.location.assign(`/materialanforderungen?id=${id}`);
        return;
      }

      if (MODAL_REF_TYPES.has(type)) {
        setFallbackRef({ type, id: refId, label });
      }
    },
    [user, groups, openProjectOverview, openDetailByBestellnummer, openTimesheetDetail, openDocumentEditor],
  );

  const fallbackModal = fallbackRef ? (
    <NoteReferenceModal
      open
      orderId={contextOrderId}
      refType={fallbackRef.type}
      refId={fallbackRef.id}
      refLabel={fallbackRef.label}
      onClose={() => setFallbackRef(null)}
    />
  ) : null;

  return { handleRefClick, fallbackModal };
}
